import React, { useState, useEffect, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInAnonymously, 
  signInWithCustomToken,
  onAuthStateChanged 
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  onSnapshot, 
  query, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { 
  Search, 
  Plus, 
  Edit, 
  Save, 
  X, 
  PieChart, 
  BarChart as BarChartIcon, 
  User, 
  LogIn, 
  LogOut,
  School,
  CheckCircle,
  AlertCircle,
  Download,
  RefreshCw,
  Calendar
} from 'lucide-react';
import sha256 from 'crypto-js/sha256';
// --- Firebase Configuration & Initialization ---
const firebaseConfig = {
  apiKey: "AIzaSyBH7gfDTEk1GloGhy4qDxQf8AFpTh5-JHc",
  authDomain: "teacherstats-project.firebaseapp.com",
  databaseURL: "https://teacherstats-project-default-rtdb.asia-southeast1.firebasedatabase.app",
  projectId: "teacherstats-project",
  storageBucket: "teacherstats-project.firebasestorage.app",
  messagingSenderId: "477959358444",
  appId: "1:477959358444:web:b5ef74e8d5367be84222c9",
  measurementId: "G-KZTGMR88HF"
};
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const appId = "my-teacher-stats";

// --- Constants ---
const MAJORS = [
  'ครูผู้ช่วยวิชาเอกคณิตศาสตร์', 'ครูผู้ช่วยวิชาเอกภาษาไทย', 'ครูผู้ช่วยวิชาเอกภาษาอังกฤษ', 'ครูผู้ช่วยวิชาเอกวิทยาศาสตร์ทั่วไป', 'ครูผู้ช่วยวิชาเอกสังคมศึกษา',
  'ครูผู้ช่วยวิชาเอกปฐมวัย/การศึกษาปฐมวัย', 'ครูผู้ช่วยวิชาเอกดนตรีไทย', 'ครูผู้ช่วยวิชาเอกดนตรีสากล', 'ครูผู้ช่วยวิชาเอกประถมศึกษา/การประถมศึกษา',
  'ครูผู้ช่วยวิชาเอกคอมพิวเตอร์', 'ครูผู้ช่วยวิชาเอกพลศึกษา','นักจัดการงานทั่วไป(พรก.)','ครูผู้ช่วย (ลูกจ้างชั่วคราว) เอกปฐมวัย','ครูผู้ช่วย (ลูกจ้างชั่วคราว) เอกประถมศึกษา','ครูผู้ช่วย (ลูกจ้างชั่วคราว) เอกภาษาไทย'
  ,'ครูผู้ช่วย (ลูกจ้างชั่วคราว) เอกคณิตศาสตร์','ครูผู้ช่วย (ลูกจ้างชั่วคราว) เอกภาษาอังกฤษ','ครูผู้ช่วย (ลูกจ้างชั่วคราว) เอกสังคมศึกษา','ครูผู้ช่วย (ลูกจ้างชั่วคราว) เอกวิทยาศาสตร์','ครูผู้ช่วย (ลูกจ้างชั่วคราว) เอกพลศึกษา'
  ,'ครูผู้ช่วย (ลูกจ้างชั่วคราว) เอกเพิ่มเติม'
];

const STATUSES = [
  'กำลังรอ', 'บรรจุแล้ว', 'สละสิทธิ์', 'บัญชีหมดอายุ', 'ระงับชั่วคราว', 'หมายเหตุอื่นๆ'
];

const STATUS_COLORS = {
  'บรรจุแล้ว': 'bg-green-100 text-green-700 border-green-200',
  'กำลังรอ': 'bg-amber-100 text-amber-700 border-amber-200',
  'สละสิทธิ์': 'bg-gray-100 text-gray-700 border-gray-200',
  'บัญชีหมดอายุ': 'bg-red-100 text-red-700 border-red-200',
  'ระงับชั่วคราว': 'bg-orange-100 text-orange-700 border-orange-200',
  'หมายเหตุอื่นๆ': 'bg-slate-100 text-slate-700 border-slate-200'
};

const CHART_COLORS = [
  '#f3cd50', '#10b981', '#23ade4', '#e24c4c', '#8b5cf6', '#64748b'
];
   // --- ฟังก์ชันคำนวณวันคงเหลือ ---
  const getDaysRemaining = (dateString) => {
    if (!dateString) return null;
    const end = new Date(dateString);
    const today = new Date();
    // คำนวณความต่างเวลา (หน่วยมิลลิวินาที -> วัน)
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
    return diffDays;
  };

  // ฟังก์ชันเลือกสีตามวันคงเหลือ
  const getDaysColor = (days) => {
    if (days === null) return 'text-gray-400';
    if (days < 0) return 'text-red-600 font-bold'; // หมดอายุ
    if (days < 90) return 'text-orange-500 font-bold'; // ใกล้หมด (3 เดือน)
    return 'text-green-600'; // ปกติ
  };

// --- Components ---

// Simple SVG Pie Chart
const SimplePieChart = ({ data }) => {
  const total = data.reduce((acc, item) => acc + item.value, 0);
  let currentAngle = 0;

  if (total === 0) return <div className="text-center text-gray-400 py-10">ไม่มีข้อมูลสำหรับกราฟ</div>;

  return (
    <div className="relative w-48 h-48 mx-auto">
      <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
        {data.map((item, index) => {
          if (item.value === 0) return null;
          const sliceAngle = (item.value / total) * 360;
          const x1 = 50 + 50 * Math.cos((Math.PI * currentAngle) / 180);
          const y1 = 50 + 50 * Math.sin((Math.PI * currentAngle) / 180);
          const x2 = 50 + 50 * Math.cos((Math.PI * (currentAngle + sliceAngle)) / 180);
          const y2 = 50 + 50 * Math.sin((Math.PI * (currentAngle + sliceAngle)) / 180);
          
          // Large arc flag
          const largeArcFlag = sliceAngle > 180 ? 1 : 0;
          
          const pathData = total === item.value 
            ? "M 50 50 m -50, 0 a 50,50 0 1,0 100,0 a 50,50 0 1,0 -100,0" // Full circle
            : `M 50 50 L ${x1} ${y1} A 50 50 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

          const path = (
            <path
              key={item.name}
              d={pathData}
              fill={item.color}
              stroke="white"
              strokeWidth="1"
            />
          );
          currentAngle += sliceAngle;
          return path;
        })}
      </svg>
      {/* Legend inside chart logic skipped for simplicity, using external legend */}
    </div>
  );
};

// Simple Bar Chart using HTML/CSS
const SimpleBarChart = ({ data }) => {
  const maxVal = Math.max(...data.map(d => d.value), 1);
  
  if (data.every(d => d.value === 0)) return <div className="text-center text-gray-400 py-10">ไม่มีข้อมูลการบรรจุ</div>;

  return (
    <div className="flex flex-col gap-2 w-full h-64 overflow-y-auto pr-2 custom-scrollbar">
      {data.map((item, index) => (
        <div key={item.name} className="flex items-center text-xs sm:text-sm">
          <div className="w-24 sm:w-32 truncate text-right mr-2 text-slate-600 font-medium" title={item.name}>
            {item.name}
          </div>
          <div className="flex-1 h-5 bg-slate-100 rounded-r-md overflow-hidden relative">
            <div 
              className="h-full bg-blue-500 rounded-r-md transition-all duration-500"
              style={{ width: `${(item.value / maxVal) * 100}%` }}
            ></div>
            <span className="absolute inset-y-0 left-2 flex items-center text-xs font-bold text-slate-700">
               {item.value > 0 ? item.value : ''}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};


export default function App() {
  const [user, setUser] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // App State
  const [isAdminMode, setIsAdminMode] = useState(() => {
    return sessionStorage.getItem('isAdminMode') === 'true';
  });
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  // Bulk Actions State
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkMajor, setBulkMajor] = useState('');
  const [bulkExpiryDate, setBulkExpiryDate] = useState('');
  // Last Updated State
  const [lastUpdated, setLastUpdated] = useState(null);
  // Filters
  const [filterYear, setFilterYear] = useState('');
  const [filterMajor, setFilterMajor] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
 
  // Form State
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    examId: '',
    rank: '',
    name: '',
    year: new Date().getFullYear() + 543, // Default to current Thai year
    major: MAJORS[0],
    status: 'กำลังรอ',
    note: '',
    expiryDate: ''
  });

  // --- Auth & Data Fetching ---
  

  useEffect(() => {
    const initAuth = async () => {
    
      await signInAnonymously(auth);
    };
    initAuth();
    
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'candidates')
      // Removed orderBy here to follow strict rule "No Complex Queries" for initial fetch
      // We will sort in memory
    );

    const unsubscribeSnapshot = onSnapshot(q, (snapshot) => {
      // 1. สร้างตัวแปรเก็บค่าเวลาสูงสุด (เริ่มที่ 0)
      let maxTime = 0;

      const items = snapshot.docs.map(doc => {
        const data = doc.data();
        
        // --- เริ่มส่วนแทรก: เช็คเวลาของแต่ละคน ---
        if (data.updatedAt) {
          let date;
          // เช็คว่าเป็น Timestamp ของ Firebase หรือไม่
          if (typeof data.updatedAt.toDate === 'function') {
            date = data.updatedAt.toDate();
          } else {
            date = new Date(data.updatedAt);
          }
          
          // ถ้าเวลาของคนนี้ ใหม่กว่าเวลาที่เราจำไว้ ให้จำเวลาคนนี้แทน
          if (date && !isNaN(date.getTime()) && date.getTime() > maxTime) {
            maxTime = date.getTime();
          }
        }
        // --- จบส่วนแทรก ---

        return {
          id: doc.id,
          ...data
        };
      });
      
      // 2. ถ้าเจอเวลาล่าสุด ให้อัปเดต State
      if (maxTime > 0) {
        setLastUpdated(new Date(maxTime));
      }

      // (โค้ดเดิม: การเรียงลำดับและ setCandidates)
      items.sort((a, b) => {
         // ... โค้ดเรียงลำดับเดิมของคุณ ...
         if (b.year !== a.year) return b.year - a.year;
         const rankA = String(a.rank);
         const rankB = String(b.rank);
         return rankA.localeCompare(rankB, undefined, { numeric: true, sensitivity: 'base' });
      });
      
      setCandidates(items);
      setLoading(false);
    });

    return () => unsubscribeSnapshot();
  }, [user]);

  // --- Logic ---

 const handleAdminLogin = (e) => {
    e.preventDefault();
    
    
    const inputHash = sha256(adminPassword).toString();

    
    const secretKey = '99959d34e4a5c6bffe632062d36fbb3accaad1fbaec0a33e85bb2de71d9e0145';
    sessionStorage.setItem('isAdminMode', 'true');
    if (inputHash === secretKey) {
      setIsAdminMode(true);
      setShowLoginModal(false);
      setAdminPassword('');
    } else {
      alert('รหัสผ่านไม่ถูกต้อง');
    }
};

  const handleLogout = () => {
    sessionStorage.removeItem('isAdminMode');
    setIsAdminMode(false);
  };
// --- เพิ่มส่วนนี้: ฟังก์ชันจัดการการเลือกและบันทึกกลุ่ม ---
  
  // 1. ฟังก์ชันติ๊กเลือกทั้งหมด (Select All)
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // เลือกทุกคนที่แสดงอยู่ตอนนี้ (ตาม Filter)
      const allIds = filteredCandidates.map(c => c.id);
      setSelectedIds(filteredCandidates.map(c => c.id));
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]); // ยกเลิกการเลือกทั้งหมด
    }
  };

  // 2. ฟังก์ชันติ๊กเลือกทีละคน
  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(itemId => itemId !== id)); // เอาออก
    } else {
      setSelectedIds([...selectedIds, id]); // เพิ่มเข้า
    }
  };

  // 3. ฟังก์ชันบันทึกการเปลี่ยนสถานะทีเดียวหลายคน (Bulk Update)
  // --- เพิ่มฟังก์ชันนี้: ส่งออกข้อมูลเป็นไฟล์ Excel (CSV) ---
  const handleExport = () => {
    if (selectedIds.length === 0) return;
    // 1. ดึงข้อมูลเฉพาะคนที่ถูกติ๊กเลือก
    const dataToExport = candidates.filter(c => selectedIds.includes(c.id));

    // 2. สร้างหัวตาราง (Header)
    const headers = ["ปีบัญชี,เลขสอบ,ลำดับที่,ชื่อ-สกุล,วิชาเอก,สถานะ,หมายเหตุ"];

    // 3. แปลงข้อมูลเป็นรูปแบบ CSV (คั่นด้วยคอมม่า)
    const rows = dataToExport.map(c => {
      // ใส่เครื่องหมายคำพูดครอบข้อความ เพื่อกันกรณีมีคอมม่าในเนื้อหา
      return [
        c.year,
        `"${c.examId}"`, // ใส่ " ครอบเพื่อกัน Excel มองเป็นตัวเลขแล้วตัด 0 นำหน้า
        `"${c.rank}"`,
        `"${c.name}"`,
        `"${c.major}"`,
        `"${c.status}"`,
        `"${c.note || ''}"`
      ].join(",");
    });

    // 4. รวมร่างเป็นก้อนข้อความ
    const csvContent = [headers, ...rows].join("\n");

    // 5. สร้างไฟล์สำหรับดาวน์โหลด (ใส่ \uFEFF เพื่อให้ Excel อ่านภาษาไทยออก)
    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    
    // 6. สั่งดาวน์โหลดอัตโนมัติ
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `รายชื่อครูผู้ช่วย_export_${new Date().toLocaleDateString('th-TH').replace(/\//g, '-')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const handleBulkUpdate = async () => {
    // เช็คว่ามีการเลือกค่าที่จะเปลี่ยนบ้างไหม (สถานะ หรือ วันที่)
    if (!bulkStatus && !bulkMajor && !bulkExpiryDate) {
      alert("กรุณาเลือกข้อมูลที่ต้องการเปลี่ยนอย่างน้อย 1 อย่าง");
      return;
    }
    if (selectedIds.length === 0) return;

    // สร้างข้อความยืนยัน
    let confirmMessage = `ยืนยันการแก้ไขข้อมูลจำนวน ${selectedIds.length} รายการ?`;
    if (bulkStatus) confirmMessage += `\n- เปลี่ยนสถานะเป็น: ${bulkStatus}`;
    if (bulkExpiryDate) confirmMessage += `\n- เปลี่ยนวันหมดอายุเป็น: ${new Date(bulkExpiryDate).toLocaleDateString('th-TH')}`;
    if (bulkMajor) confirmMessage += `\n- เปลี่ยนตำแหน่งเป็น: ${bulkMajor}`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const updates = selectedIds.map(id => {
        const ref = doc(db, 'artifacts', appId, 'public', 'data', 'candidates', id);
        
        // เตรียมข้อมูลที่จะแก้ (แก้เฉพาะที่มีค่า)
        const updateData = { updatedAt: serverTimestamp() };
        if (bulkStatus) updateData.status = bulkStatus;
        if (bulkMajor) updateData.major = bulkMajor ;
        if (bulkExpiryDate) updateData.expiryDate = bulkExpiryDate;

        return updateDoc(ref, updateData);
      });

      await Promise.all(updates);
      alert(`อัปเดตข้อมูล ${selectedIds.length} รายการ เรียบร้อยแล้ว`);
      
      // เคลียร์ค่าหลังจากทำเสร็จ
      setSelectedIds([]); 
      setBulkStatus('');
      setBulkMajor('');
      setBulkExpiryDate('');
    } catch (error) {
      console.error("Bulk update failed:", error);
      alert("เกิดข้อผิดพลาดในการอัปเดตข้อมูล");
    }
  };
  
 
  const handleSave = async (e) => {
    e.preventDefault();
    if (!user) return;

    try {
      const collectionRef = collection(db, 'artifacts', appId, 'public', 'data', 'candidates');
      
     const payload = {
        ...formData,
        rank: String(formData.rank), 
        year: Number(formData.year),
        expiryDate: formData.expiryDate,
        updatedAt: serverTimestamp()
      };

      if (editingId) {
        await updateDoc(doc(collectionRef, editingId), payload);
      } else {
        await addDoc(collectionRef, {
          ...payload,
          createdAt: serverTimestamp()
        });
      }

      setFormData({
        examId: '',
        rank: '',
        name: '',
        year: new Date().getFullYear() + 543,
        major: MAJORS[0],
        status: 'กำลังรอ',
        note: ''
      });
      setEditingId(null);
      setIsAdding(false);
    } catch (err) {
      console.error("Error saving:", err);
      alert("เกิดข้อผิดพลาดในการบันทึก");
    }
  };

  const startEdit = (candidate) => {
    setFormData({
      examId: candidate.examId,
      rank: candidate.rank !== undefined && candidate.rank !== null ? String(candidate.rank) : '',
      name: candidate.name,
      year: candidate.year,
      major: candidate.major,
      status: candidate.status,
      note: candidate.note || '',
      expiryDate: candidate.expiryDate || ''
    });
    setEditingId(candidate.id);
    setIsAdding(true);
  };

  

  const filteredCandidates = useMemo(() => {
    return candidates.filter(c => {
      const matchYear = filterYear ? c.year.toString() === filterYear : true;
      const matchMajor = filterMajor ? c.major === filterMajor : true;
      const matchStatus = filterStatus ? c.status === filterStatus : true;
      const matchSearch = searchQuery 
        ? (c.name.includes(searchQuery) || c.examId.includes(searchQuery))
        : true;
      return matchYear && matchMajor && matchStatus && matchSearch;
    });
  }, [candidates, filterYear, filterMajor, filterStatus, searchQuery]);
  

  const uniqueYears = useMemo(() => [...new Set(candidates.map(c => c.year))].sort((a,b) => b-a), [candidates]);

  // Stats Logic
  const stats = useMemo(() => {
    // 1. Status Distribution (Pie)
    const statusCounts = {};
    STATUSES.forEach(s => statusCounts[s] = 0);
    filteredCandidates.forEach(c => {
      if (statusCounts[c.status] !== undefined) statusCounts[c.status]++;
      else statusCounts['อื่นๆ']++; // Handle legacy data
    });

    const pieData = Object.keys(statusCounts).map((key, idx) => ({
      name: key,
      value: statusCounts[key],
      color: CHART_COLORS[idx % CHART_COLORS.length]
    }));

    // 2. Hired by Major (Bar) - Only count "บรรจุแล้ว"
    const majorCounts = {};
    MAJORS.forEach(m => majorCounts[m] = 0);
    
    // We calculate from *filtered* or *all*? Usually stats show context of filter. 
    // Let's use filteredCandidates to make charts interactive with filters.
    filteredCandidates.forEach(c => {
      if (c.status === 'บรรจุแล้ว') {
        if (majorCounts[c.major] !== undefined) majorCounts[c.major]++;
      }
    });

    const barData = Object.keys(majorCounts).map(key => ({
      name: key,
      value: majorCounts[key]
    })).sort((a,b) => b.value - a.value); // Sort by hired count desc

    return { pieData, barData };
  }, [filteredCandidates]);


  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800 pb-12">
      
      {/* --- Navbar / Header --- */}
      <header className="bg-white shadow-sm border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
             {/* Logo Placeholder */}
             
          
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 border border-blue-200">
          
            <img src="/logo.png" alt="Logo" className="w-12 h-12 object-contain" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-800 leading-tight">
                ระบบติดตามสถานะการบรรจุแต่งตั้งออนไลน์ (E-Appointment Tracking System) 
              </h1>
              <p className="text-xs sm:text-sm text-slate-500">
                สำนักงานเขตพื้นที่การศึกษาประถมศึกษานครสวรรค์ เขต 2
              </p>
            </div>
          </div>
          
          {/* --- จบส่วนปุ่มขวาบน --- */}
          <div className="flex">
           <button 
              onClick={() => window.location.reload()} 
              className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
              title="โหลดหน้าเว็บใหม่"
            >
              <RefreshCw size={16}/>
              <span className="hidden sm:inline">รีเฟรช</span>
            </button>
          <button 
            onClick={isAdminMode ? handleLogout : () => setShowLoginModal(true)}
            className={`flex ml-2 items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${isAdminMode ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            {isAdminMode ? <><LogOut size={16}/> ออกจากระบบ</> : <><LogIn size={16}/> จนท.</>}
          </button>
          </div>
        </div>
      </header>
      <div className="bg-indigo-50 border-b border-indigo-100 py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center items-center text-xs sm:text-sm text-blue-600">
           <span className="font-semibold mr-2"> :</span>
           <span>
             ข้อมูลล่าสุด ณ วันที่ {lastUpdated ? lastUpdated.toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) + ' น.' : '-'}
           </span>
        </div>
      </div>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 space-y-6">

        {/* --- Dashboard Stats --- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Card 1: Summary Numbers */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex flex-col justify-center">
            <h3 className="text-slate-500 text-sm font-medium mb-4 flex items-center gap-2">
              <User size={16}/> จำนวนทั้งหมด (ตามตัวกรอง)
            </h3>
            <div className="text-5xl font-bold text-blue-600 mb-2">
              {filteredCandidates.length}
            </div>
            <div className="text-sm text-slate-400">รายชื่อ</div>
            <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-2 text-xs">
               <div className="flex items-center gap-1 text-green-600 font-semibold">
                  <CheckCircle size={12} /> 
                  บรรจุแล้ว: {filteredCandidates.filter(c => c.status === 'บรรจุแล้ว').length}
               </div>
               <div className="flex items-center gap-1 text-amber-500 font-semibold">
                  <AlertCircle size={12} /> 
                  รอเรียก: {filteredCandidates.filter(c => c.status === 'กำลังรอ').length}
               </div>
               <div className="flex items-center gap-1 text-red-400 font-semibold">
                  <AlertCircle size={12} /> 
                  บัญชีหมดอายุ: {filteredCandidates.filter(c => c.status === 'บัญชีหมดอายุ').length}
               </div>
               <div className="flex items-center gap-1 text-gray-400 font-semibold">
                  <AlertCircle size={12} /> 
                  หมายเหตุอื่นๆ: {filteredCandidates.filter(c => c.status === 'หมายเหตุอื่นๆ').length}
               </div>
            </div>
          </div>

          {/* Card 2: Pie Chart */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
            <h3 className="text-slate-500 text-sm font-medium mb-4 flex items-center gap-2">
              <PieChart size={16}/> สัดส่วนสถานะ
            </h3>
            <div className="flex flex-col sm:flex-row items-center gap-4">
               <SimplePieChart data={stats.pieData} />
               <div className="text-xs space-y-1 w-full sm:w-auto">
                 {stats.pieData.map((d, i) => d.value > 0 && (
                   <div key={i} className="flex items-center gap-2">
                     <span className="w-3 h-3 rounded-full" style={{backgroundColor: d.color}}></span>
                     <span className="text-slate-600">{d.name} ({Math.round((d.value/filteredCandidates.length)*100)}%)</span>
                   </div>
                 ))}
               </div>
            </div>
          </div>

          {/* Card 3: Bar Chart */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
            <h3 className="text-slate-500 text-sm font-medium mb-4 flex items-center gap-2">
              <BarChartIcon size={16}/> จำนวนเรียกบรรจุ (แยกตามวิชาเอก)
            </h3>
            <SimpleBarChart data={stats.barData} />
          </div>
        </div>

        {/* --- Controls / Filter --- */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex flex-col md:flex-row gap-4 items-end md:items-center justify-between">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 w-full md:w-auto flex-1">
            
            {/* Search */}
            <div className="relative">
               <Search className="absolute left-3 top-2.5 text-slate-400" size={18} />
               <input 
                 type="text" 
                 placeholder="ค้นหา ชื่อ หรือ เลขสอบ..." 
                 className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                 value={searchQuery}
                 onChange={(e) => setSearchQuery(e.target.value)}
               />
            </div>

            {/* Year Filter */}
            <select 
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
            >
              <option value="">ทุกปี พ.ศ.</option>
              {uniqueYears.map(y => <option key={y} value={y}>{y}</option>)}
            </select>

            {/* Major Filter */}
            <select 
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
              value={filterMajor}
              onChange={(e) => setFilterMajor(e.target.value)}
            >
              <option value="">ตำแหน่งทั้งหมด</option>
              {MAJORS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>

             {/* Status Filter */}
             <select 
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">ทุกสถานะ</option>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>

          </div>

          {isAdminMode && (
            <button 
              onClick={() => { setIsAdding(true); setEditingId(null); setFormData(prev => ({...prev, name: '', rank: '', examId: ''})); }}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow-sm text-sm font-medium transition-colors w-full md:w-auto justify-center"
            >
              <Plus size={18} /> เพิ่มข้อมูล
            </button>
          )}
        </div>
        {/* --- เพิ่มส่วนนี้: แถบเครื่องมือจัดการกลุ่ม (แสดงเฉพาะเมื่อเป็น Admin และมีการเลือก) --- */}
        {isAdminMode && selectedIds.length > 0 && (
          <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-30 bg-slate-800 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-4 animate-bounce-in">
            <span className="text-sm font-medium whitespace-nowrap">
              เลือก {selectedIds.length} รายการ
            </span>
            <div className="h-4 w-px bg-slate-600"></div>
            
            {/* Dropdown เลือกสถานะ */}
            <select 
              className="bg-slate-700 border-none text-sm rounded px-3 py-1 text-white focus:ring-2 focus:ring-blue-500 outline-none"
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
            >
              <option value="">-- เปลี่ยนสถานะเป็น --</option>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <div className="h-4 w-px bg-slate-600"></div>
            
            {/* Dropdown เลือกเปลี่ยนตำแหน่ง */}
            <select 
              className="bg-slate-700 border-none text-sm rounded px-3 py-1 text-white focus:ring-2 focus:ring-blue-500 outline-none"
              value={bulkMajor}
            
              onChange={(e) => setBulkMajor(e.target.value)}
>
            
              <option value="">-- เปลี่ยนเป็นตำแหน่ง --</option>
              {MAJORS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          <div className="flex items-center gap-1 bg-slate-700 rounded px-2 py-1">
               <Calendar size={14} className="text-slate-400"/>
               <input 
                 type="date"
                 className="bg-transparent border-none text-xs sm:text-sm text-white focus:ring-0 outline-none p-0 w-24 sm:w-auto"
                 value={bulkExpiryDate}
                 onChange={(e) => setBulkExpiryDate(e.target.value)}
                 title="เปลี่ยนวันหมดอายุบัญชี"
               />
            </div>
            {/* ปุ่มบันทึก */}
            <button 
              onClick={handleBulkUpdate}
              className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-1 rounded text-sm font-medium transition-colors"
            >
              บันทึก
            </button>
            
            {/*  แทรกปุ่ม Export ตรงนี้ครับ  */}
            <button 
              onClick={handleExport}
              className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-1 rounded text-sm font-medium transition-colors flex items-center gap-2 ml-2"
              title="ดาวน์โหลดข้อมูลที่เลือก"
            >
              <Download size={16} /> Export
            </button>

            {/* ปุ่มยกเลิก */}
            

            <button 
              onClick={() => setSelectedIds([])}
              className="ml-2 text-slate-400 hover:text-white"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* --- Data Table --- */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-100">
                <tr>
                   {/* เพิ่มอันนี้ไว้เป็น <th> แรกสุดของหัวตาราง */}
                  {isAdminMode && (
                   <th className="px-4 py-4 w-10 text-center">
                    <input 
                      type="checkbox" 
                      onChange={handleSelectAll}
                      // ถ้าเลือกครบทุกรายการ ให้ติ๊กถูก
                      checked={filteredCandidates.length > 0 && selectedIds.length === filteredCandidates.length}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                 </th>
                 )}
                  <th className="px-6 py-4 font-medium">ปีบัญชี</th>
                  <th className="px-6 py-4 font-medium">เลขสอบ</th>
                  <th className="px-6 py-4 font-medium">ลำดับที่</th>
                  <th className="px-6 py-4 font-medium">ชื่อ-สกุล</th>
                  <th className="px-6 py-4 font-medium">ตำแหน่ง</th>
                  <th className="px-6 py-4 font-medium">สถานะ</th>
                  <th className="px-6 py-4 font-medium">อายุบัญชี</th>
                  <th className="px-6 py-4 font-medium">หมายเหตุ</th>
                  {isAdminMode && <th className="px-6 py-4 font-medium text-right">จัดการ</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={isAdminMode ? 8 : 7} className="px-6 py-10 text-center text-slate-400">
                       กำลังโหลดข้อมูล...
                    </td>
                  </tr>
                ) : filteredCandidates.length === 0 ? (
                  <tr>
                    <td colSpan={isAdminMode ? 8 : 7} className="px-6 py-10 text-center text-slate-400">
                       ไม่พบข้อมูลตามเงื่อนไข
                    </td>
                  </tr>
                ) : (
                  filteredCandidates.map((c) => (
                      <tr key={c.id} className={`hover:bg-slate-50 transition-colors ${selectedIds.includes(c.id) ? 'bg-blue-50/50' : ''}`}>
                      {isAdminMode && (
                        <td className="px-4 py-4 text-center">
                          <input 
                            type="checkbox" 
                            checked={selectedIds.includes(c.id)}
                            onChange={() => handleSelectOne(c.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                      )}
                      <td className="px-6 py-4 text-slate-500">{c.year}</td>
                      <td className="px-6 py-4 font-mono text-slate-600">{c.examId}</td>
                      <td className="px-6 py-4 font-semibold text-slate-800">#{c.rank}</td>
                      <td className="px-6 py-4 font-medium text-slate-900">{c.name}</td>
                      <td className="px-6 py-4">
                        <span className="inline-block bg-indigo-50 text-indigo-700 text-xs px-2 py-1 rounded-md border border-indigo-100">
                          {c.major}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[c.status] || STATUS_COLORS['อื่นๆ']}`}>
                           <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                           {c.status}
                        </span>
                      </td>
                      {/* แทรกต่อจาก <td>วิชาเอก</td> */}
                      <td className="px-6 py-4">
                        {(() => {
                          const days = getDaysRemaining(c.expiryDate);
                          return days !== null ? (
                            <div className="text-xs">
                              <div className={getDaysColor(days)}>
                                {days < 0 ? `หมดอายุ (${Math.abs(days)} วัน)` : `เหลือ ${days} วัน`}
                              </div>
                              <div className="text-slate-400 font-light scale-90 origin-left">
                                {new Date(c.expiryDate).toLocaleDateString('th-TH', { dateStyle: 'medium' })}
                              </div>
                            </div>
                          ) : (
                            <span className="text-slate-300">-</span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 text-slate-500 max-w-xs truncate" title={c.note}>
                        {c.note || '-'}
                      </td>
                      {isAdminMode && (
                        <td className="px-6 py-4 text-right">
                          <button 
                            onClick={() => startEdit(c)}
                            className="text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-50 rounded"
                            title="แก้ไข"
                          >
                            <Edit size={16} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </main>

      {/* --- Modals --- */}
      
      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 transform transition-all scale-100">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
               <LogIn size={20} className="text-blue-600"/> เข้าสู่ระบบเจ้าหน้าที่
            </h2>
            <form onSubmit={handleAdminLogin} className="space-y-4">
               <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">รหัสผ่าน</label>
                  <input 
                    type="password" 
                    value={adminPassword}
                    onChange={e => setAdminPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="ใส่รหัสผ่าน..."
                  />
                  <p className="text-xs text-slate-400 mt-1">Hint: </p>
               </div>
               <div className="flex gap-2 justify-end">
                 <button 
                   type="button" 
                   onClick={() => setShowLoginModal(false)}
                   className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
                 >
                   ยกเลิก
                 </button>
                 <button 
                   type="submit"
                   className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm"
                 >
                   เข้าสู่ระบบ
                 </button>
               </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {isAdding && (
         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 my-8">
              <div className="flex justify-between items-center mb-6">
                 <h2 className="text-xl font-bold flex items-center gap-2">
                    {editingId ? <Edit size={20} className="text-blue-600"/> : <Plus size={20} className="text-blue-600"/>}
                    {editingId ? 'แก้ไขข้อมูลผู้สอบ' : 'เพิ่มข้อมูลผู้สอบ'}
                 </h2>
                 <button onClick={() => setIsAdding(false)} className="text-slate-400 hover:text-slate-600">
                    <X size={24} />
                 </button>
              </div>

              <form onSubmit={handleSave} className="space-y-4">
                 <div className="grid grid-cols-2 gap-4">
                    <div>
                       <label className="block text-sm font-medium text-slate-700 mb-1">ปี พ.ศ. บัญชี</label>
                       <input 
                         type="number" 
                         required
                         className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-blue-500 outline-none"
                         value={formData.year}
                         onChange={e => setFormData({...formData, year: e.target.value})}
                       />
                    </div>
                
                 <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">วันหมดอายุบัญชี</label>
                       <input 
                         type="date" 
                         className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-blue-500 outline-none"
                         value={formData.expiryDate}
                         onChange={e => setFormData({...formData, expiryDate: e.target.value})}
                       />
                    </div>
                 </div>
                    <div>
                       <label className="block text-sm font-medium text-slate-700 mb-1">เลขประจำตัวสอบ</label>
                       <input 
                         type="text" 
                         required
                         className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-blue-500 outline-none"
                         value={formData.examId}
                         onChange={e => setFormData({...formData, examId: e.target.value})}
                       />
                    </div>
                 </div>

                 <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-1">
                       <label className="block text-sm font-medium text-slate-700 mb-1">ลำดับที่</label>
                       <input 
                         type="text" 
                         required
                         className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-blue-500 outline-none"
                         value={formData.rank}
                         onChange={e => setFormData({...formData, rank: e.target.value})}
                       />
                    </div>
                    <div className="col-span-2">
                       <label className="block text-sm font-medium text-slate-700 mb-1">ชื่อ-สกุล</label>
                       <input 
                         type="text" 
                         required
                         className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-blue-500 outline-none"
                         value={formData.name}
                         onChange={e => setFormData({...formData, name: e.target.value})}
                       />
                    </div>
                 </div>

                 <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">ตำแหน่ง</label>
                    <select 
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-blue-500 outline-none bg-white"
                      value={formData.major}
                      onChange={e => setFormData({...formData, major: e.target.value})}
                    >
                      {MAJORS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                 </div>

                 <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">สถานะ</label>
                    <select 
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-blue-500 outline-none bg-white"
                      value={formData.status}
                      onChange={e => setFormData({...formData, status: e.target.value})}
                    >
                      {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                 </div>

                 <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">หมายเหตุ (Optional)</label>
                    <textarea 
                      rows="2"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-blue-500 outline-none"
                      value={formData.note}
                      onChange={e => setFormData({...formData, note: e.target.value})}
                      placeholder="เช่น โรงเรียนบ้านหนอง..."
                    ></textarea>
                 </div>

                 <div className="pt-4 flex gap-3">
                    <button 
                      type="button" 
                      onClick={() => setIsAdding(false)}
                      className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-medium"
                    >
                      ยกเลิก
                    </button>
                    <button 
                      type="submit"
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-sm font-medium flex items-center justify-center gap-2"
                    >
                      <Save size={18} /> บันทึกข้อมูล
                    </button>
                 </div>

              </form>
            </div>
         </div>
      )}

    </div>
  );
}